import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminPayments, adminApprovePayment, adminRejectPayment } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-payments", filter],
    queryFn: () => adminPayments(filter ? { status_filter: filter } : {}),
  });
  const approveMut = useMutation({
    mutationFn: adminApprovePayment,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-payments"] }); toast({ title: "Approved" }); },
  });
  const rejectMut = useMutation({
    mutationFn: adminRejectPayment,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-payments"] }); toast({ title: "Rejected" }); },
  });

  return (
    <div className="space-y-3" data-testid="admin-payments-tab">
      <div className="flex gap-2">
        {["", "pending", "verified", "failed"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            data-testid={`admin-payments-filter-${s || "all"}`}
            className={`px-3 py-1 rounded-full text-xs ${filter === s ? "bg-amber-400/90 text-ink-900" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            {s || "all"}
          </button>
        ))}
      </div>
      {isLoading ? <Loader2 className="size-5 animate-spin text-white/50" /> : (
        <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="admin-payments-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/40">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2 text-left">Plan</th>
                <th className="px-3 py-2 text-left">Tx</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(data?.payments || []).map((p) => (
                <tr key={p.id} className="bg-white/[0.02]" data-testid={`admin-payment-row-${p.id}`}>
                  <td className="px-3 py-2 text-xs text-white/60">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-white/80 text-xs">{p.user_email}</td>
                  <td className="px-3 py-2 text-amber-200 text-xs">{p.plan_key} · ${p.amount_usdt}</td>
                  <td className="px-3 py-2 text-xs font-mono">{(p.tx_hash || "").slice(0, 14)}… <span className="text-white/30">({p.network})</span></td>
                  <td className="px-3 py-2 text-xs">
                    {p.status === "verified" && <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="size-3.5" /> verified</span>}
                    {p.status === "pending"  && <span className="text-amber-300 inline-flex items-center gap-1">pending</span>}
                    {p.status === "failed"   && <span className="text-red-300 inline-flex items-center gap-1"><XCircle className="size-3.5" /> failed</span>}
                  </td>
                  <td className="px-3 py-2 space-x-1">
                    {p.status !== "verified" && (
                      <button onClick={() => approveMut.mutate(p.id)} className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                        data-testid={`admin-approve-${p.id}`}>Approve</button>
                    )}
                    {p.status === "pending" && (
                      <button onClick={() => rejectMut.mutate(p.id)} className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-200 hover:bg-red-500/30"
                        data-testid={`admin-reject-${p.id}`}>Reject</button>
                    )}
                  </td>
                </tr>
              ))}
              {!(data?.payments || []).length && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-white/40 text-sm">No payments.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
